import { twMerge } from "tailwind-merge";
import { motion } from "motion/react";
import { Helmet } from "react-helmet-async";
import { Loader } from "@themes/pollinator/ui/Loader";
import { PlatformAppFooter } from "./PlatformAppFooter";
import { PlatformSidebar } from "./PlatformSidebar";
import { SupportArticleSheetContainer, SupportDialogs } from "@platform/features/support";
import { MarketingDialogsContainer } from "@platform/features/marketing";
import { ChatSheetContainer } from "@platform/features/chat";
import { useSupportSheet } from "../store";

export type Props = {
  children?: React.ReactNode;
  className?: string;
  header?: React.ReactNode;
  sidebar?: React.ReactNode;
  isWithFooter?: boolean;
  isWithSidebar?: boolean;
  title: string;
  isLoading?: boolean;
};

export function AppLayout({
  children,
  className,
  header,
  sidebar,
  isLoading,
  isWithFooter = true,
  isWithSidebar = false,
  title,
}: Props) {
  const { isOpen } = useSupportSheet();

  return (
    <>
      <Helmet>
        <title>{["Pollinator", title].filter(Boolean).join(" | ")}</title>
      </Helmet>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3, ease: "easeIn" }}
        className="flex flex-row"
      >
        <div
          className={twMerge(
            "min-h-[100dvh] flex flex-col bg-brand-50 text-brand-800 w-full",
            className
          )}
        >
          {/* Legacy header prop support */}
          {header}

          {/* Sidebar (includes mobile header + desktop sidebar) */}
          {isWithSidebar && <PlatformSidebar>{sidebar}</PlatformSidebar>}

          {/* Main content area - shifts right when sidebar is present */}
          <div
            className={twMerge(
              "flex flex-col flex-1 w-full h-full relative",
              isWithSidebar ? "pt-14 md:pt-0 md:pl-64" : ""
            )}
          >
            <main className="flex-grow flex flex-col">
              {isLoading ? <Loader /> : children}
            </main>
            {isWithFooter && <PlatformAppFooter />}
          </div>
        </div>

        <MarketingDialogsContainer />
        {isOpen && <SupportArticleSheetContainer />}
        <SupportDialogs />

        <ChatSheetContainer />
      </motion.div>
    </>
  );
}

export default AppLayout;
